<template>
    <div>
        <h2
            class="text-lg font-medium leading-7 tracking-[-0.02em] text-iron-700"
        >
            {{ $t("configurator.calendars") }}
        </h2>

        <p class="text-sm tracking-[-0.01em] text-iron-500">
            {{ $t("configurator.calendars_description") }}
        </p>

        <div class="mt-8 grid gap-1">
            <div v-if="integrationCalendars.length > 0">
                <Label> {{ $t("event_types.calendars.new_bookings") }} </Label>
                <Select
                    :disabled="loading || !integrationCalendarsLoaded"
                    :model-value="selectedIntegrationCalendar"
                    @update:model-value="onSelectedIntegrationUpdate"
                >
                    <SelectTrigger class="w-full">
                        <SelectValue
                            :placeholder="
                                $t('event_types.calendars.choose_calendar')
                            "
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup
                            v-for="calendarIntegration of integrationCalendars"
                        >
                            <SelectLabel class="flex items-center gap-2 pl-2">
                                <GoogleCalendar
                                    v-if="
                                        calendarIntegration.integration.type ===
                                        'GOOGLE'
                                    "
                                />

                                {{
                                    calendarIntegration.integration.name
                                }}</SelectLabel
                            >
                            <SelectItem
                                v-for="calendar of calendarIntegration.calendars"
                                :value="`${calendarIntegration.integration.id}-${calendar.id.toString()}`"
                            >
                                {{ calendar.name }}
                            </SelectItem>
                        </SelectGroup>
                    </SelectContent>
                </Select>
            </div>
            <div v-if="integrationCalendars.length > 0" class="mt-4">
                <Label> {{ $t("event_types.calendars.conflicts") }} </Label>

                <div
                    v-show="integrationCalendarsLoaded"
                    v-for="integrationCalendar of integrationCalendars"
                    class="mt-1 rounded-md border border-iron-200 p-4"
                >
                    <div class="flex gap-3">
                        <GoogleCalendar></GoogleCalendar>
                        <div class="flex flex-col">
                            <div class="text-sm font-medium text-iron-700">
                                {{ integrationCalendar.integration.name }}
                            </div>
                            <div class="text-xs tracking-[-1%] text-iron-500">
                                {{ integrationCalendar.integrationEmail }}
                            </div>

                            <div
                                class="mt-2"
                                v-for="calendar of integrationCalendar.calendars"
                                :key="calendar.id"
                            >
                                <ConflictSwitch
                                    :calendar="calendar"
                                    :integration-id="
                                        integrationCalendar.integration.id
                                    "
                                    :event-type-user="eventTypeUser"
                                    :event-type-id="eventType.id"
                                ></ConflictSwitch>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-show="!integrationCalendarsLoaded">
                <div
                    class="mx-auto mt-1 w-full rounded-md border border-stone-200 p-3 shadow"
                >
                    <div class="flex animate-pulse space-x-2">
                        <div class="h-10 w-10 rounded-md bg-stone-200"></div>
                        <div class="max-w-[220px] flex-1 space-y-6 py-1">
                            <div class="space-y-3">
                                <div class="grid grid-cols-3 gap-4">
                                    <div
                                        class="col-span-2 h-3 rounded bg-stone-200"
                                    ></div>
                                </div>
                            </div>
                            <div class="space-y-3">
                                <div class="h-3 rounded bg-stone-200"></div>
                                <div class="h-3 rounded bg-stone-200"></div>
                                <div class="h-3 rounded bg-stone-200"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="
                    integrationCalendars.length <= 0 &&
                    integrationCalendarsLoaded
                "
            >
                <Link href="/integrations">
                    <PromoBanner :is-paid="false">
                        <template #title>{{
                            $t("event_types.calendars.banner.title")
                        }}</template>
                        <template #description>{{
                            $t("event_types.calendars.banner.description")
                        }}</template>
                        <template #icon>
                            <CalendarOff :size="20" class="text-[#E92F34]" />
                        </template>
                    </PromoBanner>
                </Link>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import axios from "axios";
import { onMounted, ref } from "vue";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import PromoBanner from "../../../Atomic/PromoBanner.vue";
import { CalendarOff } from "lucide-vue-next";
import { Link } from "@inertiajs/vue3";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { Label } from "@/components/ui/label";
import ConflictSwitch from "./components/ConflictSwitch.vue";

import type {
    AssignIntegrationRequest,
    Calendar,
    IntegrationCalendar,
} from "../../types/IntegrationCalendar";
import GoogleCalendar from "../../../Integrations/components/logos/GoogleCalendar.svg";

import type { EventType } from "../../types/EventType";
import type { EventTypeUser } from "../../types/EventTypeUser";
import { toast } from "@/components/ui/toast";

interface Props {
    eventType: EventType;
    eventTypeUser: EventTypeUser;
}

const props = defineProps<Props>();

const integrationCalendars = ref<IntegrationCalendar[]>([]);
const integrationCalendarsLoaded = ref(false);
const selectedIntegrationCalendar = ref<string>();
const loading = ref(false);

onMounted(async () => {
    integrationCalendars.value = await getIntegrationCalendars();

    selectIntegrationCalendarIfExists();

    integrationCalendarsLoaded.value = true;
});

function selectIntegrationCalendarIfExists() {
    // @TODO: legacy typecheck when integration_data used to be unparsed string
    if (
        typeof props.eventTypeUser.integration_data === "string" ||
        !props.eventTypeUser.integration_data?.calendars
    ) {
        return;
    }

    const calendarId = props.eventTypeUser.integration_data?.calendars[0]?.id;
    const integrationId = props.eventTypeUser.integration_id;

    selectedIntegrationCalendar.value = `${integrationId}-${calendarId}`;
}

async function getIntegrationCalendars() {
    try {
        const res = await axios.get(route("app.integrations.calendars.index"));

        return res.data.data;
    } catch (err) {
        toast({
            title: "Uh oh! Something went wrong. Please contact support.",
            description: "There was a problem with your request.",
            variant: "destructive",
        });
    }
}

function onSelectedIntegrationUpdate(value: string) {
    const splitText = value.split("-");

    if (splitText.length !== 2) {
        toast({
            title: `Error when assigning calendar`,
            description: `Split chunk is too long, expected 2, got ${splitText.length}`,
            variant: "destructive",
        });

        return;
    }

    const [integrationId, calendarId] = splitText;

    const integrationCalendar = integrationCalendars.value.find(
        (integrationCalendar) => {
            return (
                integrationCalendar.integration.id === parseInt(integrationId)
            );
        },
    );

    if (!integrationCalendar) {
        toast({
            title: `Error when assigning calendar`,
            description: `Integration calendar not found`,
            variant: "destructive",
        });
        return;
    }

    const calendar = integrationCalendar.calendars.find(
        (calendar) => calendar.id.toString() === calendarId,
    );

    if (!calendar) {
        toast({
            title: `Error when assigning calendar`,
            description: `Calendar not found`,
            variant: "destructive",
        });
        return;
    }

    assignIntegrationCalendar(parseInt(integrationId), calendar);
}

function assignIntegrationCalendar(
    calendarIntegrationId: number,
    calendar: Calendar,
) {
    loading.value = true;

    const data: AssignIntegrationRequest = {
        calendars: [
            {
                integration_id: calendarIntegrationId,
                integration_calendar_id: calendar.id,
                integration_calendar_name: calendar.name,
                integration_calendar_default: calendar.default,
            },
        ],
    };

    router.post(
        route("app.event-types.calendar-integrations.assign", {
            eventType: props.eventType.id,
        }),
        data,
        {
            onFinish: () => {
                selectIntegrationCalendarIfExists();
                loading.value = false;
            },
            onError: (err) => {
                toast({
                    title: "Uh oh! Something went wrong. Please contact support.",
                    description:
                        "There was a problem with your request." +
                        JSON.stringify(err),
                    variant: "destructive",
                });
            },
        },
    );
}
</script>
